import { useState } from "react";
import { ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { store, useStore } from "@/lib/store";
import type { PlatformProject } from "@/lib/platformTypes";
import { cn } from "@/lib/utils";
import { ConnectPlatformCard, EmptyState, ViewSection, relativeTime } from "@/views/shared";

function statusBadge(status: string | null | undefined) {
  const archived = status === "archived";
  return (
    <Badge
      variant="outline"
      className={cn(
        "shrink-0 font-mono text-[10px] uppercase tracking-wider",
        archived
          ? "border-border text-muted-foreground"
          : "border-primary/40 bg-primary/10 text-primary",
      )}
    >
      {status ?? "unknown"}
    </Badge>
  );
}

function ProjectRow({ project }: { project: PlatformProject }) {
  return (
    <button
      type="button"
      onClick={() => store.navigate({ name: "project", id: project.id })}
      className="glass-border flex w-full flex-col gap-1 rounded-xl p-3 text-left transition-colors hover:bg-secondary/40"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-sm font-medium">{project.name}</span>
        {statusBadge(project.status)}
      </div>
      <div className="flex items-center justify-between gap-2">
        {project.url ? (
          <a
            href={project.url}
            target="_blank"
            rel="noreferrer"
            // Opening the live site must not also navigate into the detail view.
            onClick={(e) => e.stopPropagation()}
            className="inline-flex min-w-0 items-center gap-1 font-mono text-xs text-primary hover:underline"
          >
            <span className="truncate">{project.url.replace(/^https?:\/\//, "")}</span>
            <ExternalLink className="h-3 w-3 shrink-0" />
          </a>
        ) : (
          <span className="text-xs text-muted-foreground">no live URL</span>
        )}
        {project.lastDeployAt && (
          <span className="shrink-0 text-xs text-muted-foreground">
            {relativeTime(project.lastDeployAt)}
          </span>
        )}
      </div>
    </button>
  );
}

export function ProjectsView() {
  const state = useStore();
  const { available, projects } = state.platform;
  const [showArchived, setShowArchived] = useState(false);

  if (!available) return <ConnectPlatformCard />;

  if (!projects.loaded) {
    return (
      <div className="flex flex-col gap-2 p-4">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-16 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  const active = projects.items.filter((p) => p.status !== "archived");
  const archived = projects.items.filter((p) => p.status === "archived");

  return (
    <div className="flex flex-col p-4">
      {projects.error && (
        <p className="mb-3 text-sm text-destructive">{projects.error}</p>
      )}

      <ViewSection title="Projects">
        {active.length === 0 ? (
          <EmptyState message="No deployed projects yet. Ask Claude to deploy a site with the OpZero MCP tools and it will show up here." />
        ) : (
          <div className="flex flex-col gap-2">
            {active.map((p) => (
              <ProjectRow key={p.id} project={p} />
            ))}
          </div>
        )}
      </ViewSection>

      {archived.length > 0 && (
        <section>
          <button
            type="button"
            onClick={() => setShowArchived((v) => !v)}
            className="mb-2 flex items-center gap-1 text-xs font-semibold uppercase tracking-widest text-muted-foreground hover:text-foreground"
          >
            {showArchived ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            Archived ({archived.length})
          </button>
          {showArchived && (
            <div className="flex flex-col gap-2">
              {archived.map((p) => (
                <ProjectRow key={p.id} project={p} />
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
